import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { strategyGet, strategyMeta, strategySave, strategyDetectIndustry, strategyAnalytics, strategyFillWeek } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Target, Sparkle, FacebookLogo, InstagramLogo, LinkedinLogo, Minus, Plus, Lightning,
  ChartBar, FilmSlate, SlidersHorizontal, FloppyDisk, MagicWand,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const currentMonth = () => new Date().toISOString().slice(0, 7);

function MixStepper({ ct, value, onChange }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-white/8 bg-white/[0.03] px-3 py-2.5" data-testid={`mix-row-${ct.key}`}>
      <div className="min-w-0 flex-1 pr-3">
        <div className="text-sm text-white flex items-center gap-1.5">
          {ct.key === "reel" && <FilmSlate size={13} className="text-pink-400" />}
          {ct.key === "carousel" && <SlidersHorizontal size={13} className="text-sky-400" />}
          {ct.label}
          <span className="text-[9px] text-[#7a7a7a]">~{ct.best_time}</span>
        </div>
        <div className="text-[10px] text-[#7a7a7a] truncate">{ct.brief}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button data-testid={`mix-dec-${ct.key}`} onClick={() => onChange(Math.max(0, value - 1))}
          className="h-7 w-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-[#9a9a9a] hover:text-white transition-colors"><Minus size={12} /></button>
        <span data-testid={`mix-val-${ct.key}`} className={`w-6 text-center text-sm font-bold ${value > 0 ? "brand-text" : "text-[#5a5a5a]"}`}>{value}</span>
        <button data-testid={`mix-inc-${ct.key}`} onClick={() => onChange(Math.min(14, value + 1))}
          className="h-7 w-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-[#9a9a9a] hover:text-white transition-colors"><Plus size={12} /></button>
      </div>
    </div>
  );
}

function AnalyticsBars({ platform, data, labels }) {
  const keys = [...new Set([...Object.keys(data.planned || {}), ...Object.keys(data.actual || {})])];
  if (!keys.length) return <div className="text-xs text-[#5a5a5a]">No planned or scheduled posts this month.</div>;
  const max = Math.max(1, ...keys.map((k) => Math.max(data.planned?.[k] || 0, data.actual?.[k] || 0)));
  return (
    <div className="space-y-2.5" data-testid={`strategy-analytics-${platform}`}>
      {keys.map((k) => (
        <div key={k}>
          <div className="flex items-center justify-between text-[10px] mb-1">
            <span className="text-[#cfcfcf]">{labels[k] || k}</span>
            <span className="text-[#7a7a7a]">planned {data.planned?.[k] || 0} · actual {data.actual?.[k] || 0}{data.published?.[k] ? ` · ${data.published[k]} live` : ""}</span>
          </div>
          <div className="flex gap-1 items-center">
            <div className="h-1.5 rounded-full bg-white/10 flex-1 overflow-hidden">
              <div className="h-full bg-white/30" style={{ width: `${((data.planned?.[k] || 0) / max) * 100}%` }} />
            </div>
            <div className="h-1.5 rounded-full bg-white/10 flex-1 overflow-hidden">
              <div className="h-full bg-[#6B3DFF]" style={{ width: `${((data.actual?.[k] || 0) / max) * 100}%` }} />
            </div>
          </div>
        </div>
      ))}
      {data.balance_score != null && (
        <div className="text-[10px] text-[#9a9a9a] pt-1" data-testid={`strategy-balance-${platform}`}>
          Mix balance: <span className={data.balance_score >= 70 ? "text-emerald-400 font-semibold" : "text-amber-300 font-semibold"}>{data.balance_score}%</span> — how closely this month's calendar matches your mix
        </div>
      )}
    </div>
  );
}

export default function ContentStrategyPage() {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const [tab, setTab] = useState("instagram");
  const [draft, setDraft] = useState(null);

  const metaQ = useQuery({ queryKey: ["strategy-meta"], queryFn: strategyMeta, staleTime: Infinity });
  const stratQ = useQuery({ queryKey: ["strategy", activeId], queryFn: () => strategyGet(activeId), enabled: !!activeId });
  const analyticsQ = useQuery({
    queryKey: ["strategy-analytics", activeId],
    queryFn: () => strategyAnalytics(activeId, currentMonth()), enabled: !!activeId,
  });

  useEffect(() => { if (stratQ.data) setDraft(JSON.parse(JSON.stringify(stratQ.data))); }, [stratQ.data]);

  const save = useMutation({
    mutationFn: () => strategySave({ client_id: activeId, industry: draft.industry, autofill: draft.autofill, platforms: draft.platforms }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["strategy", activeId] });
      qc.invalidateQueries({ queryKey: ["strategy-analytics", activeId] });
      toast.success("Content Strategy saved — Monthly Planner & Auto Pilot now follow this mix");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save strategy"),
  });
  const detect = useMutation({
    mutationFn: () => strategyDetectIndustry(activeId),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["strategy", activeId] });
      setDraft((d) => ({ ...d, industry: r.industry, industry_detected: r.industry, industry_confidence: r.confidence, industry_reasoning: r.reasoning }));
      toast.success(`Industry detected: ${r.label} (${Math.round((r.confidence || 0) * 100)}% confident)`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Detection failed"),
  });
  const fill = useMutation({
    mutationFn: () => strategyFillWeek(activeId),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["strategy-analytics", activeId] });
      toast.success(r.created ? `${r.created} post(s) generated for the next 7 days — reels wait in the Approval Queue` : (r.reason || "Nothing to fill"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Fill failed"),
  });

  const meta = metaQ.data;
  const industries = meta?.industries || [];
  const contentTypes = useMemo(() => meta?.content_types || [], [meta]);
  const goals = meta?.goals || [];
  const labels = useMemo(() => Object.fromEntries(contentTypes.map((c) => [c.key, c.label])), [contentTypes]);

  if (!draft || !meta) return <PageContainer><div className="text-sm text-[#7a7a7a] p-8">Loading strategy…</div></PageContainer>;

  const pconf = draft.platforms?.[tab] || { enabled: false, goal: "brand_awareness", weekly_mix: {} };
  const setPlatform = (patch) => setDraft((d) => ({ ...d, platforms: { ...d.platforms, [tab]: { ...d.platforms[tab], ...patch } } }));
  const setMix = (key, val) => setPlatform({ weekly_mix: { ...pconf.weekly_mix, [key]: val } });
  const weeklyTotal = Object.values(pconf.weekly_mix || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const industryTpl = industries.find((i) => i.key === draft.industry);
  const anyEnabled = Object.values(draft.platforms || {}).some((p) => p.enabled && Object.values(p.weekly_mix || {}).some((v) => v > 0));

  return (
    <PageContainer>
      <PageHeader icon={Target} eyebrow="AI Command" title="Content Strategy"
        subtitle="Define WHAT your AI posts, not just how often. Set a weekly content mix per platform — lifestyle, product, behind-the-scenes, reels, carousels — pick a business goal, and Vozelto rotates types intelligently across your calendar." />

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Industry intelligence */}
          <div className="card-matte p-5" data-testid="strategy-industry-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><MagicWand size={16} className="brand-text" weight="fill" />
                <span className="font-head font-semibold">Industry Intelligence</span></div>
              <Button data-testid="strategy-detect-btn" size="sm" variant="ghost" onClick={() => detect.mutate()} disabled={detect.isPending}
                className="text-xs border border-[#6B3DFF]/40 brand-text hover:bg-[#6B3DFF]/10 h-8 rounded-full px-3">
                <Sparkle size={13} className="mr-1" weight="fill" /> {detect.isPending ? "Detecting…" : "Detect with AI"}
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <div className="label-caps mb-1">Industry</div>
                <select data-testid="strategy-industry-select" value={draft.industry || ""} onChange={(e) => setDraft((d) => ({ ...d, industry: e.target.value }))}
                  className="w-full h-10 rounded-lg bg-[#10101A] border border-white/10 px-3 text-sm">
                  <option value="" className="bg-[#161622]">Not set</option>
                  {industries.map((i) => <option key={i.key} value={i.key} className="bg-[#161622]">{i.label}</option>)}
                </select>
              </div>
              {industryTpl && (
                <div className="rounded-lg bg-white/[0.03] border border-white/8 p-3 text-[11px] text-[#9a9a9a]">
                  {industryTpl.hints}
                  <button data-testid="strategy-apply-template" onClick={() => { setPlatform({ enabled: true, weekly_mix: { ...industryTpl.mix } }); toast.success(`${industryTpl.label} mix applied to ${tab}`); }}
                    className="block mt-1.5 brand-text hover:text-[#A56CFF] font-semibold">Apply recommended {industryTpl.label} mix to {tab === "facebook" ? "Facebook" : "Instagram"} →</button>
                </div>
              )}
            </div>
            {draft.industry_reasoning && <div className="mt-2 text-[10px] text-[#7a7a7a]" data-testid="strategy-industry-reasoning">AI: {draft.industry_reasoning}</div>}
          </div>

          {/* Per-platform mix */}
          <div className="card-matte p-5" data-testid="strategy-mix-card">
            <div className="flex items-center gap-2 mb-4">
              {["instagram", "facebook", "linkedin"].map((p) => (
                <button key={p} data-testid={`strategy-tab-${p}`} onClick={() => setTab(p)}
                  className={`flex items-center gap-1.5 text-sm font-semibold px-4 h-9 rounded-full border transition-colors ${tab === p ? "bg-[#6B3DFF] border-[#6B3DFF] text-white" : "bg-white/5 border-white/10 text-[#9a9a9a] hover:text-white"}`}>
                  {p === "instagram" ? <InstagramLogo size={15} /> : p === "facebook" ? <FacebookLogo size={15} /> : <LinkedinLogo size={15} />}
                  {p === "instagram" ? "Instagram" : p === "facebook" ? "Facebook" : "LinkedIn"}
                  {draft.platforms?.[p]?.enabled && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}
                </button>
              ))}
              <label className="ml-auto flex items-center gap-2 text-xs text-[#9a9a9a] cursor-pointer">
                <span>{pconf.enabled ? "Strategy ON" : "Strategy OFF"}</span>
                <button data-testid={`strategy-enable-${tab}`} onClick={() => setPlatform({ enabled: !pconf.enabled })} role="switch" aria-checked={pconf.enabled}
                  className={`relative h-6 w-11 rounded-full transition-colors ${pconf.enabled ? "bg-emerald-500" : "bg-white/15"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${pconf.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </label>
            </div>

            <div className="label-caps mb-2">Business goal — shapes every caption & CTA on {tab === "facebook" ? "Facebook" : "Instagram"}</div>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="strategy-goals">
              {goals.map((g) => (
                <button key={g.key} data-testid={`strategy-goal-${g.key}`} onClick={() => setPlatform({ goal: g.key })} title={g.guidance}
                  className={`h-8 rounded-full px-3.5 text-xs font-semibold border transition-colors ${pconf.goal === g.key ? "bg-[#6B3DFF] border-[#6B3DFF] text-white" : "bg-white/5 border-white/10 text-[#9a9a9a] hover:text-white"}`}>
                  {g.label}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between mb-2">
              <div className="label-caps">Weekly content mix</div>
              <div className="text-xs" data-testid="strategy-week-total">
                <span className={`font-bold ${weeklyTotal > 0 ? "brand-text" : "text-[#5a5a5a]"}`}>{weeklyTotal}</span>
                <span className="text-[#7a7a7a]"> posts / week</span>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {contentTypes.map((ct) => (
                <MixStepper key={ct.key} ct={ct} value={Number(pconf.weekly_mix?.[ct.key] || 0)} onChange={(v) => setMix(ct.key, v)} />
              ))}
            </div>
            <div className="mt-3 text-[10px] text-[#7a7a7a]">
              Reels generate a filmable storyboard (hook, scenes, audio) and wait for your video — Vozelto never fakes footage. Carousels generate 3-5 slides and use your real Media Library photos.
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button data-testid="strategy-save-btn" onClick={() => save.mutate()} disabled={save.isPending}
                className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-10 px-5">
                <FloppyDisk size={15} className="mr-1.5" weight="fill" /> {save.isPending ? "Saving…" : "Save Strategy"}
              </Button>
              <Button data-testid="strategy-fill-week-btn" variant="ghost" onClick={() => fill.mutate()} disabled={fill.isPending || !anyEnabled}
                className="rounded-full border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 h-10 px-4 text-sm">
                <Lightning size={15} className="mr-1.5" weight="fill" /> {fill.isPending ? "Generating…" : "Fill next 7 days now"}
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          {/* AutoPilot autofill */}
          <div className="card-matte p-5" data-testid="strategy-autofill-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Lightning size={15} className="text-emerald-400" weight="fill" />
                <span className="font-head font-semibold text-sm">Auto Pilot Weekly Autofill</span></div>
              <button data-testid="strategy-autofill-toggle" onClick={() => setDraft((d) => ({ ...d, autofill: !d.autofill }))} role="switch" aria-checked={!!draft.autofill}
                className={`relative h-6 w-11 rounded-full transition-colors ${draft.autofill ? "bg-emerald-500" : "bg-white/15"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${draft.autofill ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            <p className="mt-2 text-[11px] text-[#9a9a9a] leading-relaxed">
              When Auto Pilot is active, Vozelto tops up the next 7 days to your mix automatically — only generating what's missing. Reels always wait in the Approval Queue for your video. Remember to Save after toggling.
            </p>
          </div>

          {/* Analytics */}
          <div className="card-matte p-5" data-testid="strategy-analytics-card">
            <div className="flex items-center gap-2 mb-1"><ChartBar size={15} className="brand-text" weight="fill" />
              <span className="font-head font-semibold text-sm">This Month's Mix — Planned vs Actual</span></div>
            <div className="text-[10px] text-[#7a7a7a] mb-3 flex items-center gap-3">
              <span className="flex items-center gap-1"><span className="h-1.5 w-4 rounded-full bg-white/30 inline-block" /> planned</span>
              <span className="flex items-center gap-1"><span className="h-1.5 w-4 rounded-full bg-[#6B3DFF] inline-block" /> actual</span>
            </div>
            {["instagram", "facebook", "linkedin"].map((p) => (
              <div key={p} className="mb-4">
                <div className="text-xs font-semibold text-[#cfcfcf] flex items-center gap-1.5 mb-2">
                  {p === "instagram" ? <InstagramLogo size={13} className="text-pink-400" /> : p === "facebook" ? <FacebookLogo size={13} className="text-sky-400" /> : <LinkedinLogo size={13} className="text-[#0A66C2]" />}
                  {p === "instagram" ? "Instagram" : p === "facebook" ? "Facebook" : "LinkedIn"}
                </div>
                {analyticsQ.data?.platforms?.[p]
                  ? <AnalyticsBars platform={p} data={analyticsQ.data.platforms[p]} labels={labels} />
                  : <div className="text-xs text-[#5a5a5a]">Loading…</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
